import { lazy, Suspense } from "react";
import { LazyLoadingAnimation } from "./Lazy";
import type { editor } from "monaco-editor";
const MonacoEditorBase = lazy(() => import("./MonacoEditor"));
// ================================================================================================
export type ChangeHandler = (
    value: string,
    event: editor.IModelContentChangedEvent
) => void;

export type ValidationHandler = (hasErrors?: boolean) => void;

//-------------------------------------------------------------------------------------------------
export type EditorDidMount = (
    editor: editor.IStandaloneCodeEditor,
    Monaco: any
) => void;

export type EditorWillMount = (m: any) => editor.IEditorOptions;
//-------------------------------------------------------------------------------------------------
export interface MonacoEditorProps {
    /**
     * Width of editor. Defaults to 100%.
     */
    width?: string | number;

    /**
     * Height of editor. Defaults to 500.
     */
    height?: string | number;

    /**
     * The initial value of the auto created model in the editor.
     */
    defaultValue?: string;

    /**
     * The initial language of the auto created model in the editor. Defaults to 'javascript'.
     */
    language?: string;

    /**
     * Theme to be used for rendering.
     * The current out-of-the-box available themes are: 'vs' (default), 'vs-dark', 'hc-black'.
     * You can create custom themes via `monaco.editor.defineTheme`.
     */
    theme?: string | null;
    /**
     * Value of the auto created model in the editor.
     * If you specify value property, the component behaves in controlled mode. Otherwise, it behaves in uncontrolled mode.
     */
    value?: string;

    /**
     * The original value that we need to see the difference with.
     * If you specify originalValue property, the component open in diff mode.
     */
    originalValue?: string;

    /**
     * Refer to Monaco interface {monaco.editor.IEditorConstructionOptions}.
     */
    options?: editor.IEditorOptions;

    /**
     * An event emitted when the editor has been mounted (similar to componentDidMount of React).
     */
    editorDidMount?: EditorDidMount;

    /**
     * An event emitted before the editor mounted (similar to componentWillMount of React).
     */
    editorWillMount?: EditorWillMount;

    /**
     * An event emitted when the content of the current model has changed.
     */
    onChange?: ChangeHandler;

    /**
     * Add extraLib.
     */
    defaultTypeString?: string;

    /**
     * External definition library
     */
    externalLibrary?: { content: string, filename: string }[]

    /**
     * menu/keyboard actions
     */
    actions?: {
        /**
         * An unique identifier of the contributed action.
         */
        id: string;
        /**
         * A label of the action that will be presented to the user.
         */
        label: string;
        /**
         * Precondition rule.
         */
        precondition?: string;
        /**
         * An array of keybindings for the action.
         */
        keybindings?: number[];
        /**
         * The keybinding rule (condition on top of precondition).
         */
        keybindingContext?: string;
        /**
         * Control if the action should show up in the context menu and where.
         * The context menu of the editor has these default:
         *   navigation - The navigation group comes first in all cases.
         *   1_modification - This group comes next and contains commands that modify your code.
         *   9_cutcopypaste - The last default group with the basic editing commands.
         * You can also create your own group.
         * Defaults to null (don't show in context menu).
         */
        contextMenuGroupId?: string;
        /**
         * Control the order in the context menu group.
         */
        contextMenuOrder?: number;
        /**
         * Method that will be executed when the action is triggered.
         * @param editor The editor instance is passed in as a convenience
         */
        run(editor: any): void | Promise<void>;
    }[];

    decorations?: {
        range: {
            /**
             * Line number on which the range starts (starts at 1).
             */
            readonly startLineNumber: number;
            /**
             * Column on which the range starts in line `startLineNumber` (starts at 1).
             */
            readonly startColumn: number;
            /**
             * Line number on which the range ends.
             */
            readonly endLineNumber: number;
            /**
             * Column on which the range ends in line `endLineNumber`.
             */
            readonly endColumn: number;
        };

        options: {
            /**
             * Should the decoration expand to encompass a whole line.
             */
            isWholeLine?: boolean;

            /**
             * CSS class name describing the decoration.
             */
            className?: string | null;

            /**
             * If set, the decoration will be rendered in the lines decorations with this CSS class name.
             */
            linesDecorationsClassName?: string | null;

            /**
             * If set, the decoration will be rendered in the glyph margin with this CSS class name.
             */
            glyphMarginClassName?: string | null;
        };
    }[];

    /**
     * Validation schemas
     */
    schemas?: {
        /**
         * The URI of the schema, which is also the identifier of the schema.
         */
        readonly uri: string;
        /**
         * A list of glob patterns that describe for which file URIs the JSON schema will be used.
         * '*' and '**' wildcards are supported. Exclusion patterns start with '!'.
         * For example '*.schema.json', 'package.json', '!foo*.schema.json', 'foo/**\/BADRESP.json'.
         * A match succeeds when there is at least one pattern matching and last matching pattern does not start with '!'.
         */
        readonly fileMatch?: string[];
        /**
         * The schema for the given URI.
         */
        readonly schema?: any;
    }[];

    /**
     * Configure when we allow saving while errors occured
     */
    isStrictSaved?: boolean;

    /**
     * An event emitted when errors detected through validation.
     */
    onValidate?: ValidationHandler;
}
// ================================================================================================
export var MonacoEditor = (props: MonacoEditorProps) => {
    return (
        <Suspense
            fallback={
                <LazyLoadingAnimation
                    style={{
                        width: props.width,
                        height: props.height,
                        backgroundColor: "#1e1e1e",
                    }}
                />
            }
        >
            <MonacoEditorBase {...props} />
        </Suspense>
    );
}